import { useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { languageData as languageDataDummy } from 'assets/dataDummy/header';
import { OptionType } from 'components/molecules/Pulldown';
import i18n from 'i18n';
import { LanguageKey } from 'services/systems/types';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import {
  resetDevelopMessage,
  setDevelopMessage,
  setLanguage, toggleIsDeveloping,
} from 'store/system';
import {
  AC_ROUTE_DETAIL,
} from 'utils/constants';
import { getImageURL } from 'utils/functions';
import {
  checkActiveLang,
  getAcDashboardURL,
  getHomeLangURL,
  getLangURL,
  langLabel,
} from 'utils/language';

export type FlowType = 'main' | 'ac' | 'ac-detail';

const useLanguage = () => {
  const { push } = useHistory();
  const { search, pathname } = useLocation();
  const query = new URLSearchParams(search);

  const stockSymbol = query.get('stock_symbol');

  const dispatch = useAppDispatch();
  const dataSystem = useAppSelector((state) => state.system.dataSystem);
  const pageTranslation = useAppSelector(
    (state) => state.system.pageTranslation,
  );
  const language = useAppSelector((state) => state.system.language);

  //* List languages
  const languageData: OptionType[] = useMemo(() => {
    if (dataSystem?.locales) {
      return (Object.keys(dataSystem.locales) as Array<LanguageKey>).map(
        (ele, index) => {
          const icon = dataSystem.locales ? dataSystem.locales[ele].icon : '';
          return {
            id: `${ele}-${index}`,
            icon: getImageURL(icon),
            value: ele,
            label: langLabel(ele, language),
          };
        },
      );
    }
    return languageDataDummy;
  }, [dataSystem, language]);

  //* Functions
  const handleChangeLang = (item: OptionType, flow?: FlowType) => {
    const languageKey = item.value as LanguageKey;
    if (checkActiveLang(languageKey, dataSystem?.locales)) {
      i18n.changeLanguage(languageKey, () => {
        dispatch(setLanguage(languageKey));
        handleLogicChangeLang(languageKey, flow);
      });
    } else {
      const message = dataSystem?.locales
        ? dataSystem.locales[languageKey].message
        : '';
      handleShowModal(languageKey, message);
    }
  };

  const checkFlowAC = (langParam: LanguageKey) => {
    if (pageTranslation) {
      const transData = pageTranslation.find((ele) => ele.locale === langParam);
      if (transData?.slug) {
        push(
          `${getLangURL(transData?.locale)}${transData?.slug || ''}`,
        );
      } else {
        push(`${getAcDashboardURL(langParam)}`);
      }
    } else {
      push(`${getHomeLangURL(langParam)}`);
    }
  };

  const checkPageTranslation = (
    langParam: LanguageKey,
    transDataParam?: TranslationDataTypes,
  ) => {
    if (transDataParam?.slug) {
      push(
        `${getLangURL(transDataParam?.locale)}${
          transDataParam?.slug !== '/' ? `/${transDataParam?.slug || ''}` : ''
        }`,
      );
    } else push(`${getHomeLangURL(langParam)}`);
  };

  const handleLogicChangeLang = async (
    langParam: LanguageKey,
    flow?: FlowType,
  ) => {
    if (flow === 'ac') {
      checkFlowAC(langParam);
    } else if (flow === 'ac-detail') {
      const routeAC = AC_ROUTE_DETAIL.find(
        (route) => pathname.includes(route.en) || pathname.includes(route.vi),
      );

      if (routeAC) {
        push(`${langParam === 'vi' ? routeAC.vi : `/en${routeAC.en}`}?stock_symbol=${stockSymbol}`);
      }
    } else if (pageTranslation) {
      const transData = pageTranslation.find((ele) => ele.locale === langParam);
      checkPageTranslation(langParam, transData);
    } else {
      push(`${getHomeLangURL(langParam)}`);
    }
  };

  const handleShowModal = useCallback(
    (langParam: LanguageKey, message?: string, callBack?: () => void) => {
      if (message) {
        dispatch(setDevelopMessage(message));
      }
      dispatch(toggleIsDeveloping(true));

      setTimeout(() => {
        dispatch(toggleIsDeveloping(false));
        dispatch(resetDevelopMessage());
        if (callBack) {
          callBack();
        }
      }, 2000);
    },
    [dispatch],
  );

  //* Memos
  const lang = useMemo(
    () => languageData.find((item) => item.value === language) || languageData[0],
    [languageData, language],
  );

  return {
    lang,
    languageData,
    handleChangeLang,
    handleShowModal,
  };
};

export default useLanguage;
